import { Button, Icon } from '@aqarmap/aqarmap-ui';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import ErrorMessage from '~/components/ErrorMessage';
import ListLoader from '~/components/ListLoader';
import SuccessMessage from '~/components/SuccessMessage';
import { useQuery } from '~/hooks';
import {
  useAcceptProposalMutation,
  useRejectProposalMutation,
  useGetProposalQuery,
} from '~/redux/api/aqarConnectApiSlice';

export default function ProposalConfirm() {
  const { t, i18n } = useTranslation('app');
  const locale = i18n.language;
  const { id } = useParams();
  const { _hash } = useQuery();

  const {
    data: proposal,
    isLoading: isProposalLoading,
    isSuccess: isProposalSuccess,
  } = useGetProposalQuery({ id, locale: locale, _hash });

  const [
    acceptProposal,
    {
      isLoading: isAcceptLoading,
      isSuccess: isAcceptSuccess,
      isError: isAcceptError,
    },
  ] = useAcceptProposalMutation();

  const [
    rejectProposal,
    {
      isLoading: isRejectLoading,
      isSuccess: isRejectSuccess,
      isError: isRejectError,
    },
  ] = useRejectProposalMutation();

  const handleProposalAccept = async () => {
    await acceptProposal({
      proposalId: id,
      _hash,
      _locale: locale,
    });
  };

  const handleProposalReject = async () => {
    await rejectProposal({
      proposalId: id,
      _hash,
      _locale: locale,
    });
  };

  useEffect(() => {
    document.title = `${t('acceptRequest')}|${t('aqarmap')}`;
  }, []);

  if (isProposalLoading || !isProposalSuccess)
    return <ListLoader count={5} height={24} />;

  return (
    <div className="layout-boundary pt-4x pb-[40px] md:pb-[80px]">
      <div className="layout-boundary__inner flex flex-col gap-4x px-2x md:px-0">
        {isAcceptSuccess && <SuccessMessage message={t('proposalAccepted')} />}
        {(isAcceptError || isRejectError) && (
          <ErrorMessage message={t('somethingWrong')} />
        )}
        {isRejectSuccess && <ErrorMessage message={t('proposalIsRejected')} />}

        <div className="flex flex-col gap-5x">
          <p className="text-title_2 text-gray__dark_2 font-bold">
            {t('readyBrokerTitle')}
          </p>
          <div className="flex flex-col gap-3x">
            <div className="flex flex-col gap-x ">
              <p className="text-body_2 text-gray__dark_1">
                {t('helloUser', { user: proposal?.requested_to?.full_name })}
              </p>
              <p className="text-title_3 font-bold">
                {t('readyBrokerMessage', {
                  location: proposal?.user_segment?.location?.title,
                })}
              </p>
            </div>
            <div className="py-3x  border-b border-t border-gray">
              <div className="flex gap-2x">
                <div className="flex gap-x">
                  {proposal?.requested_by.logo ? (
                    <img
                      src={proposal?.requested_by.logo}
                      alt="Account-Img"
                      className="w-[56px] h-[56px] rounded-full object-contain "
                    />
                  ) : (
                    <div className="rounded-full bg-gray_light_1 border border-gray w-[56px] h-[56px] element-center">
                      <Icon
                        name="user"
                        className="text-gray__dark_1"
                        size="32px"
                      />
                    </div>
                  )}
                  <div className="flex flex-col gap-0.5x">
                    <p className="text-title_4 text-gray__dark_2 font-bold">
                      {proposal?.requested_by?.full_name}
                    </p>
                    <p className="text-body_2 text-gray__dark_1">
                      {t('listingsCount', {
                        count: proposal?.requested_by?.activeListingsCount,
                      })}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex gap-x text-body_1 text-orange_dark_2 ">
              <div className="w-[8px] h-[8px] bg-orange_light_1 rounded-[50%] mt-0.5x"></div>
              {t('confirmNote')}
            </div>
            <div className="flex gap-2x">
              <Button
                size="medium"
                variant="primary"
                className="w-auto"
                onClick={handleProposalAccept}
                isLoading={isAcceptLoading}
                disabled={isAcceptLoading || isRejectLoading}
              >
                {t('acceptText')}
              </Button>
              <Button
                size="medium"
                variant="link"
                className="w-auto text-deco"
                isLoading={isRejectLoading}
                disabled={isAcceptLoading || isRejectLoading}
                onClick={handleProposalReject}
              >
                {t('cancelText')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
